import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Logo } from "../components/SiteNav";
import api, { apiErr, track } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";

const HERO = "https://images.unsplash.com/photo-1576280314550-773c50583407?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBmaWxtJTIwcHJvZHVjdGlvbiUyMGRhcmt8ZW58MHx8fHwxNzg3Nzc3MDU5fDA&ixlib=rb-4.1.0&q=85";

function Shell({ children, title, sub }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden grain overflow-hidden lg:block">
        <img src={HERO} alt="" className="h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#050508] via-black/60 to-transparent" />
        <div className="absolute bottom-14 left-14 max-w-[420px]">
          <p className="font-display text-3xl font-semibold leading-tight">
            Imagine. Direct. Create.
          </p>
          <p className="mono mt-4 text-[11px] uppercase tracking-[0.2em] text-[#9194a6]">
            Studio Movies IA — Your AI Film Studio
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-[400px]">
          <Link to="/">
            <Logo />
          </Link>
          <h1 className="mt-12 font-display text-3xl font-semibold">{title}</h1>
          {sub && <p className="mt-3 text-sm text-[#9194a6]">{sub}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "mt-2 w-full rounded-lg border border-[#212230] bg-[#101118] px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#ff4b2b]/60";

function GoogleButton({ label }) {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      onClick={go}
      data-testid="google-login-btn"
      className="btn-ghost mt-4 flex w-full items-center justify-center gap-3 rounded-lg py-3 text-sm font-semibold"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
      </svg>
      {label}
    </button>
  );
}

export function Login() {
  const { locale, t } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(f.email, f.password);
      track("login");
      nav(u.role === "admin" ? "/admin" : "/studio");
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title={t("auth.signin")} sub={locale === "fr" ? "Reprenez votre production." : "Pick up your production."}>
      <Seo title={`${t("auth.signin")} — Studio Movies IA`} description="Sign in" locale={locale} />
      <form onSubmit={submit} className="mt-9 space-y-4" data-testid="login-form">
        <div>
          <label className="overline">{t("auth.email")}</label>
          <input
            type="email"
            required
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            data-testid="login-email"
            className={inputCls}
          />
        </div>
        <div>
          <label className="overline">{t("auth.password")}</label>
          <input
            type="password"
            required
            value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })}
            data-testid="login-password"
            className={inputCls}
          />
        </div>
        {err && (
          <p data-testid="login-error" className="rounded-lg border border-[#ff4b4b]/30 bg-[#ff4b4b]/10 px-4 py-2.5 text-[13px] text-[#ff8a8a]">
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="btn-primary w-full rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? "…" : t("auth.signin")}
        </button>
      </form>
      <div className="mono my-5 text-center text-[11px] uppercase tracking-[0.2em] text-[#8288a0]">
        {t("auth.or")}
      </div>
      <GoogleButton label={t("auth.google")} />
      <div className="mt-8 flex justify-between text-[13px] text-[#9194a6]">
        <Link to={`/${locale}/forgot-password`} className="hover:text-white">
          {t("auth.forgot")}
        </Link>
        <Link to={`/${locale}/register`} data-testid="goto-register" className="hover:text-white">
          {t("auth.noAccount")}
        </Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { locale, t } = useI18n();
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await register(f);
      track("signup");
      nav("/studio");
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title={t("auth.signup")}
      sub={locale === "fr" ? "1 vidéo de 5 secondes offerte. Aucune carte requise." : "1 free 5-second video. No card required."}
    >
      <Seo title={`${t("auth.signup")} — Studio Movies IA`} description="Create account" locale={locale} />
      <form onSubmit={submit} className="mt-9 space-y-4" data-testid="register-form">
        <div>
          <label className="overline">{t("auth.name")}</label>
          <input
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            data-testid="register-name"
            className={inputCls}
          />
        </div>
        <div>
          <label className="overline">{t("auth.email")}</label>
          <input
            type="email"
            required
            value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            data-testid="register-email"
            className={inputCls}
          />
        </div>
        <div>
          <label className="overline">{t("auth.password")}</label>
          <input
            type="password"
            required
            minLength={6}
            value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })}
            data-testid="register-password"
            className={inputCls}
          />
        </div>
        {err && (
          <p data-testid="register-error" className="rounded-lg border border-[#ff4b4b]/30 bg-[#ff4b4b]/10 px-4 py-2.5 text-[13px] text-[#ff8a8a]">
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="register-submit"
          className="btn-amber w-full rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? "…" : t("auth.signup")}
        </button>
      </form>
      <div className="mono my-5 text-center text-[11px] uppercase tracking-[0.2em] text-[#8288a0]">
        {t("auth.or")}
      </div>
      <GoogleButton label={t("auth.google")} />
      <p className="mt-8 text-[13px] text-[#9194a6]">
        {t("auth.hasAccount")}{" "}
        <Link to={`/${locale}/login`} data-testid="goto-login" className="text-white hover:underline">
          {t("auth.signin")}
        </Link>
      </p>
    </Shell>
  );
}

export function ForgotPassword() {
  const { locale } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      toast.error(apiErr(e2));
    }
  };

  return (
    <Shell title={locale === "fr" ? "Mot de passe oublié" : "Reset your password"}>
      {sent ? (
        <p className="mt-8 rounded-lg border border-[#00ff9d]/25 bg-[#00ff9d]/[0.06] p-5 text-[13.5px] text-[#9affd0]" data-testid="forgot-sent">
          {locale === "fr"
            ? "Si le compte existe, un lien de réinitialisation a été envoyé."
            : "If the account exists, a reset link has been sent."}
        </p>
      ) : (
        <form onSubmit={submit} className="mt-9 space-y-4">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@studio.com"
            data-testid="forgot-email"
            className={inputCls}
          />
          <button type="submit" data-testid="forgot-submit" className="btn-primary w-full rounded-lg py-3 text-sm font-semibold">
            {locale === "fr" ? "Envoyer le lien" : "Send reset link"}
          </button>
        </form>
      )}
      <Link to={`/${locale}/login`} className="mt-8 block text-[13px] text-[#9194a6] hover:text-white">
        ← {locale === "fr" ? "Connexion" : "Sign in"}
      </Link>
    </Shell>
  );
}

export function ResetPassword() {
  const { locale } = useI18n();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: sp.get("token"), password: pw });
      toast.success(locale === "fr" ? "Mot de passe mis à jour" : "Password updated");
      nav(`/${locale}/login`);
    } catch (e2) {
      toast.error(apiErr(e2));
    }
  };

  return (
    <Shell title={locale === "fr" ? "Nouveau mot de passe" : "New password"}>
      <form onSubmit={submit} className="mt-9 space-y-4">
        <input
          type="password"
          required
          minLength={6}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          data-testid="reset-password"
          className={inputCls}
        />
        <button type="submit" data-testid="reset-submit" className="btn-primary w-full rounded-lg py-3 text-sm font-semibold">
          {locale === "fr" ? "Mettre à jour" : "Update password"}
        </button>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    if (!sid) {
      nav("/en/login", { replace: true });
      return;
    }
    api
      .post("/auth/session", { session_id: sid }, { headers: { "X-Session-ID": sid } })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", "/studio");
        nav("/studio", { replace: true, state: { user: data } });
      })
      .catch(() => nav("/en/login", { replace: true }));
  }, [nav, setUser]);

  return (
    <div className="grid min-h-screen place-items-center" data-testid="auth-callback">
      <p className="mono text-[11px] uppercase tracking-[0.24em] text-[#9194a6]">Entering the studio…</p>
    </div>
  );
}

export function PaymentResult({ ok }) {
  const { locale } = useI18n();
  const [sp] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const started = useRef(false);

  useEffect(() => {
    if (!ok || started.current) return;
    started.current = true;
    const sid = sp.get("session_id");
    if (!sid) {
      setStatus("failed");
      return;
    }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 12) setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
  }, [ok, sp, refresh]);

  return (
    <div className="grid min-h-screen place-items-center px-6" data-testid={ok ? "payment-success" : "payment-cancel"}>
      <div className="max-w-[440px] text-center">
        <h1 className="font-display text-3xl font-semibold">
          {!ok
            ? locale === "fr" ? "Paiement annulé" : "Payment cancelled"
            : status === "paid"
            ? locale === "fr" ? "Crédits ajoutés" : "Credits added"
            : status === "failed"
            ? locale === "fr" ? "Session introuvable" : "Session not found"
            : locale === "fr" ? "Confirmation en cours…" : "Confirming payment…"}
        </h1>
        <p className="mt-5 text-sm text-[#9194a6]">
          {!ok
            ? locale === "fr" ? "Aucun montant n'a été débité." : "Nothing was charged."
            : status === "paid"
            ? locale === "fr" ? "Vos Movies Credits sont disponibles dans le studio." : "Your Movies Credits are live in the studio."
            : locale === "fr" ? "Merci de patienter quelques secondes." : "This takes a few seconds."}
        </p>
        <a href="/studio" className="btn-amber mt-9 inline-block rounded-full px-7 py-3.5 text-sm font-semibold">
          {locale === "fr" ? "Retour au studio" : "Back to the studio"}
        </a>
      </div>
    </div>
  );
}
